// @ts-strict-ignore
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { Group, Material, Mesh, Object3D, Vector3 } from 'three';
import { ArticleObject3DManager } from '@article/manager/article-object-3d.manager';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleModelDataHolder, ParsedArticleModelData } from '@3d-scene/model3d/cache/model-data';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ReplaceArticleModelOperation } from '../../operation/replace-model-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { MetaElementUsageVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { Injectable } from '@angular/core';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { AdmintoolOperationFactory } from '../../operation/admintool-operation.factory';
import { ArrayUtils } from '@shared/util/array-utils';
import { TextureElementVO } from '@article/model/vo/texture-element.vo';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

@Injectable({ providedIn: 'root' })
export class Replace3DModelHelper {
  private static readonly REPLACE_MODEL_ROTATION: Vector3 = new Vector3(0, 180, 0);
  private static readonly REPLACE_ZIP_MODEL_SCALE: number = 10;
  private static readonly REPLACE_GLTF_MODEL_SCALE: number = 10000;

  constructor(
    private projectModel: ProjectModel,
    private articleObject3DManager: ArticleObject3DManager,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private containerService: ContainerService,
    private applicationRefreshService: ApplicationRefreshService,
    private admintoolOperationFactory: AdmintoolOperationFactory
  ) {}

  public async modelParseCallbackFn(
    parsedArticleModelData: ParsedArticleModelData,
    articleVO: ArticleVO,
    keepConnections: boolean = false,
    applyDefaultTransformationFn: (object: Object3D) => void | undefined = undefined,
  ): Promise<void> {
    if (parsedArticleModelData && parsedArticleModelData.modelData) {
      const articleUsage: ArticleUsageVO = this.projectModel.getCurrentLoadedArticleUsage();
      const copyMeta: MetaElementVO[] = [...articleVO.metaElements];
      const copyMetaUsages: MetaElementUsageVO[] = [...articleUsage.metaElementUsages];
      const copyTextures: TextureElementVO[] = [...articleVO.textureElements];

      if (!keepConnections) {
        if (articleVO) {
          articleVO.metaElements = [];
        }
        if (articleUsage) {
          articleUsage.metaElementUsages = [];
        }
      }
      this.selectionModelService.removeAllSelections();
      if (applyDefaultTransformationFn) {
        applyDefaultTransformationFn(parsedArticleModelData.modelData);
      }
      this.removeConnectionMeshesFromModel(parsedArticleModelData.modelData);

      if (articleUsage) {
        const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
        const oldModelDataHolder: ArticleModelDataHolder = this.createArticleModelDataHolderByModelData(
          articleContainer.model,
          articleContainer.name,
          articleVO
        );

        if (keepConnections) {
          this.addConnectionsFromArticleToModel(articleContainer, parsedArticleModelData.modelData);
        }
        const articleModelDataHolder: ArticleModelDataHolder = this.createArticleModelDataHolderByModelData(
          parsedArticleModelData.modelData,
          parsedArticleModelData.modelData.name,
          articleVO,
          '.obz'
        );
        this.articleObject3DManager.addArticleObject3DData(articleModelDataHolder);
        articleContainer.isParsed = false;
        articleContainer.disposeModel();
        await articleContainer.parseModel(articleModelDataHolder);
        articleContainer.addAxesHelper();
        await this.applicationRefreshService.refreshProject();
        this.projectModel.notifyCurrentProjectChanged();
        this.applicationRefreshService.notifyProjectChange();

        const replaceArticleModelOperation: ReplaceArticleModelOperation = this.admintoolOperationFactory.createReplaceArticleModelOperation(
          articleContainer,
          oldModelDataHolder,
          articleModelDataHolder,
          copyMeta,
          copyMetaUsages,
          copyTextures,
          keepConnections
        );

        if (replaceArticleModelOperation != null) {
          await this.undoRedoHelper.pushUndoOperation(replaceArticleModelOperation);
        }
      }
    }
  }

  public async modelParseCallbackFnGltf(
    parsedArticleModelData: ParsedArticleModelData,
    articleVO: ArticleVO,
    keepConnections: boolean = false
  ): Promise<void> {
    return this.modelParseCallbackFn(
      parsedArticleModelData,
      articleVO,
      keepConnections,
      (object: Object3D) => this.applyDefaultGltfTransformation(object),
    );
  }

  public async modelParseCallbackFnZip(
    parsedArticleModelData: ParsedArticleModelData,
    articleVO: ArticleVO,
    keepConnections: boolean = false
  ): Promise<void> {
    return this.modelParseCallbackFn(
      parsedArticleModelData,
      articleVO,
      keepConnections,
      (object: Object3D) => this.applyDefaultZipTransformation(object),
    );
  }

  private cloneModelData(modelData: Group): Group {
    if (modelData) {
      const obj: Group = modelData.clone() as Group;
      obj.children.forEach((child) => {
        if (IsThreeJsType.mesh(child)) {
          if (MeshUtils.isConnection(child.name)) {
            child.material = (child.material as Material).clone();
            Object3DUtils.disposeAllChildren(child);
          }
        }
      });
      return obj;
    }
    return null;
  }

  private createArticleModelDataHolderByModelData(
    modelData: any,
    name,
    article: ArticleVO,
    type: string = '',
    sourceUrl: string = ''
  ): ArticleModelDataHolder {
    const modelCopy: Group = this.cloneModelData(modelData);
    const parsedModelDataHolder: ParsedArticleModelData = new ParsedArticleModelData(modelCopy, name);
    return new ArticleModelDataHolder(article, parsedModelDataHolder, type, sourceUrl);
  }

  private applyDefaultZipTransformation(object: Object3D): void {
    this.applyDefaultTransformation(object, Replace3DModelHelper.REPLACE_MODEL_ROTATION, Replace3DModelHelper.REPLACE_ZIP_MODEL_SCALE);
  }

  private applyDefaultGltfTransformation(object: Object3D): void {
    this.applyDefaultTransformation(object, Replace3DModelHelper.REPLACE_MODEL_ROTATION, Replace3DModelHelper.REPLACE_GLTF_MODEL_SCALE);
  }

  private applyDefaultTransformation(object: Object3D, rotation: Vector3, scale: number): void {
    if (!object) {
      return;
    }
    if (IsThreeJsType.mesh(object)) {
      object.geometry.rotateX(MathUtils.toRadians(rotation.x));
      object.geometry.rotateY(MathUtils.toRadians(rotation.y));
      object.geometry.rotateZ(MathUtils.toRadians(rotation.z));
      object.geometry.scale(scale, scale, scale);
    }
    if (object.children) {
      object.children.forEach((ch: any) => this.applyDefaultTransformation(ch, rotation, scale));
    }
  }

  private removeConnectionMeshesFromModel(model: Object3D): void {
    if (model) {
      const meshesToRemove: Mesh[] = model.children.filter((ch) => IsThreeJsType.mesh(ch) && MeshUtils.isConnection(ch.name)) as Mesh[];
      model.remove(...meshesToRemove);
      meshesToRemove.forEach((m) => Object3DUtils.dispose(m));
    }
  }

  private addConnectionsFromArticleToModel(articleContainer: ArticleContainer, model: Object3D): void {
    if (articleContainer && model) {
      const connectionMeshes: Mesh[] = articleContainer.connections
        .filter((c) => !!c.mesh)
        .map((c) => {
          const clonedMesh: Mesh = c.mesh.clone() as Mesh;
          Object3DUtils.disposeAllChildren(clonedMesh);
          clonedMesh.geometry.applyMatrix4(clonedMesh.matrixWorld);
          clonedMesh.position.set(0, 0, 0);
          clonedMesh.rotation.set(0, 0, 0);
          return clonedMesh;
        });
      if (ArrayUtils.isNotEmpty(connectionMeshes)) {
        model.add(...connectionMeshes);
      }
    }
  }
}
